import { createErrorToastMapper, type SaaSConfig } from '@xhubio-saas/core-frontend/kernel'
import { SettingsAccountPageWithProviders } from './app.pages'
import { plugins } from './app.plugins.js'
import { AuthBrandPanel } from './components/AuthBrandPanel'
import { BillingEntitlementProvider } from './components/BillingEntitlementProvider'
import { InvoiceApiLogo } from './components/InvoiceApiLogo'
import { LocaleWrapper } from './components/LocaleWrapper'
import { OnboardingCompletionListener } from './components/OnboardingCompletionListener'
import { VerifactuNavGate } from './components/VerifactuNavGate'
import { withConsentGuard } from './components/withConsentGuard'
import { redirectTo } from './layouts/redirectTo'
import { ClarityAnalytics } from './lib/analytics/ClarityAnalytics'
import { cookieCategories } from './lib/analytics/clarityCookies'
import { versandkategorienLocales } from './locales/versandkategorienLocales'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { LoginPage } from './pages/auth/LoginPage'
import { RegisterPage } from './pages/auth/RegisterPage'
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage'
import { ProfilePage } from './pages/protected/ProfilePage'
import { SettingsAppearancePage } from './pages/protected/settings/SettingsAppearancePage'
import { SettingsEmailPage } from './pages/protected/settings/SettingsEmailPage'
import { SettingsPasswordPage } from './pages/protected/settings/SettingsPasswordPage'
import { SettingsProfilePage } from './pages/protected/settings/SettingsProfilePage'
import { SettingsSystemPage } from './pages/protected/settings/SettingsSystemPage.js'
import { CookiePolicyPage } from './pages/public/CookiePolicyPage'
import { ImprintPage } from './pages/public/ImprintPage'
import { LicensesPage } from './pages/public/LicensesPage'
import { PrivacyPage } from './pages/public/PrivacyPage'
import { TermsPage } from './pages/public/TermsPage'
import { UnsubscribePage } from './pages/public/UnsubscribePage'

// Wrap protected pages with consent guard
const ProtectedProfilePage = withConsentGuard(ProfilePage)
const ProtectedSettingsProfilePage = withConsentGuard(SettingsProfilePage)
const ProtectedSettingsPasswordPage = withConsentGuard(SettingsPasswordPage)
const ProtectedSettingsAppearancePage = withConsentGuard(SettingsAppearancePage)
const ProtectedSettingsAccountPage = withConsentGuard(SettingsAccountPageWithProviders)
const ProtectedSettingsSystemPage = withConsentGuard(SettingsSystemPage)
const ProtectedSettingsEmailPage = withConsentGuard(SettingsEmailPage)

function getApiUrl(): string {
  // Runtime config takes precedence (set at container startup)
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.API_URL) {
    return window.__RUNTIME_CONFIG__.API_URL.replace(/\/+$/, '')
  }
  // Build-time env var (for local development)
  // Empty string means "use relative URLs" (goes through Vite proxy)
  if (import.meta.env.VITE_API_URL !== undefined) {
    return import.meta.env.VITE_API_URL.replace(/\/+$/, '')
  }
  // In test environment, return empty string
  if (import.meta.env.MODE === 'test' || typeof window === 'undefined') {
    return ''
  }
  throw new Error('API URL not configured. Set VITE_API_URL or provide runtime config.')
}

/**
 * SaaS Application Configuration
 *
 * This configuration is used by the frontend-kernel to bootstrap
 * the application with all necessary providers and features.
 */
// biome-ignore lint/suspicious/noExplicitAny: SaaSConfig is parameterised via this open record so plugins can extend it
export const saasConfig: SaaSConfig & Record<string, any> = {
  // ═══════════════════════════════════════════════════════════════════════════
  // Branding
  // ═══════════════════════════════════════════════════════════════════════════
  name: 'Invoice-api.xhub.io',
  logo: InvoiceApiLogo,

  // ═══════════════════════════════════════════════════════════════════════════
  // API Configuration
  // ═══════════════════════════════════════════════════════════════════════════
  api: {
    baseUrl: getApiUrl(),
    trpcPath: '/trpc',
    /**
     * Ohne Mapper reicht der Kernel die **rohe Backend-Meldung** an den Toast
     * durch — englische Entwicklersaetze und Zod-JSON inklusive. invoice-api
     * hatte als einzige produktive App gar keinen (Befund Plan 131 S5).
     *
     * Bewusst **ohne** Feld-Beschriftungen: die gaebe es hier nur doppelt, die
     * Klartexte stehen schon an den Formularen. Fehlt eine, greift der
     * generische Satz — besser als ein roher Schema-Schluessel.
     */
    mapErrorToast: createErrorToastMapper({ devWarnings: import.meta.env.DEV }),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Authentication
  // ═══════════════════════════════════════════════════════════════════════════
  auth: {
    providers: ['email', 'google', 'github'],
    emailVerification: true,
    allowRegistration: true,
    // invoice-api owns its auth screens; the kernel routes to these instead of
    // its built-in forms. Needs core-frontend >= 3.36.0 — on 3.35.0 the key is
    // ignored and the built-in forms render, so this degrades rather than
    // breaks. The other three apps keep the kernel default.
    //
    // Owning `register` means owning the consent capture: RegisterPage mirrors
    // the kernel's contract (terms + privacy mandatory → `consentTypes`). If
    // the required documents change, both must change.
    components: {
      login: LoginPage,
      register: RegisterPage,
      forgotPassword: ForgotPasswordPage,
      verifyEmail: VerifyEmailPage,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Theme
  // ═══════════════════════════════════════════════════════════════════════════
  theme: {
    defaultTheme: 'system',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Languages (European languages for invoice application)
  // ═══════════════════════════════════════════════════════════════════════════
  languages: {
    default: 'en',
    active: [
      'en', // English
      'de', // German
      'nl', // Dutch
      'cs', // Czech
      'bg', // Bulgarian
      'fr', // French
      'es', // Spanish
      'it', // Italian
      'pl', // Polish
      'pt', // Portuguese
      'hu', // Hungarian
      'ro', // Romanian
    ],
    // Die app-eigene Versandkategorie `report` (IA-BE `src/email/dispatch-categories.ts`) in allen zwölf Sprachen —
    // keine Überschreibung eines Plugin-Textes, sondern Schlüssel, die nur das App-Backend kennt. Herkunft des
    // Wortlauts und Wächter: `src/locales/versandkategorienLocales.ts`.
    overrides: {
      emailOutbox: versandkategorienLocales,
    },
    // Der Begriff dieser App für „die Organisation, in der der Anwender ist"
    // (Plan CORE 2026-09-14/01 S4): **„Organisation"** — bewusst das
    // Rahmenwerkswort, bis jemand anderes entscheidet. Steht ausdrücklich hier,
    // damit die Stelle existiert und nicht still der Kernel-Rückfall greift;
    // tests/organisationsbegriff.test.ts prüft, dass beide dasselbe Wort führen.
    // Nur de/en — die übrigen Sprachen fallen auf den Kernel zurück, der
    // dasselbe Wort führt. Formen mit Bestimmwort nach `OrganizationTermForms`.
    organizationTerm: {
      de: {
        nominativ: 'die Organisation',
        genitiv: 'dieser Organisation',
        dativ: 'dieser Organisation',
        akkusativ: 'die Organisation',
        kompositum: 'Organisations',
      },
      en: {
        nominativ: 'the organization',
        genitiv: 'of this organization',
        dativ: 'this organization',
        akkusativ: 'the organization',
        kompositum: 'organization',
      },
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Features
  // ═══════════════════════════════════════════════════════════════════════════
  features: {
    quickActions: true,
    preferences: true,
    breadcrumbs: true,
    organizationSwitcher: false,
    // Abschnitt „Organisation exportieren“ auf der Kontoseite (core-frontend ≥ 5.74.0); das Backend liefert
    // `complianceOrganizationExport` und die Download-Route (ALLGEMEIN 09-26/03 S6).
    organisationsExport: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Layout
  // ═══════════════════════════════════════════════════════════════════════════
  layout: {
    // Conversion-First: Login (und der Abschluss des Setups) landet direkt im
    // Konvertierungs-Playground — dort erledigt der Entwickler seinen ersten
    // echten Job. Das Nutzungs-Dashboard (/dashboard) und die Quota-Detailseite
    // (/billing) bleiben je einen Klick entfernt.
    defaultRoute: '/invoice-conversion',
    // Kein Footer im eingeloggten Bereich: seine vier Punkte (Impressum,
    // Datenschutz, AGB, Cookie-Einstellungen) wohnen in Einstellungen → System
    // → Rechtliches (SettingsSystemPage). Login/Register/Public behalten den
    // Footer — Impressumspflicht, und dort sitzen Sprach-/Theme-Wahl für
    // Ausgeloggte. footerLinks/copyright unten bleiben dafür erhalten.
    hideProtectedFooter: true,
    // Das Avatar-Menue fuehrt nur das Persoenliche; das Organisatorische steht
    // in der Seitenleiste unter „Organisation → System".
    userMenu: { settingsLabelKey: 'common.myAccount' },
    // Split-screen for the auth screens: AuthLayout renders this as the left
    // lg:w-1/2 column and drops it below lg, so the form owns the phone. Needs
    // no kernel change — the hook has existed since 3.34.0 (hausverwaltung uses
    // it too) and narrows the form column to max-w-md when set.
    authAside: AuthBrandPanel,
    // Feedback in the sidebar footer above "Collapse" (core-frontend layout.feedback, unreleased —
    // tsc stays red until the console pins the core-frontend release that ships the field).
    feedback: { placement: 'sidebar' },
    sidebar: {
      upgrade: {
        enabled: true,
        // Straight into the plans tab of the billing page (plan API 2026-10-11/02, E5).
        href: '/billing?tab=plans',
        labelKey: 'common:sidebar.upgrade',
      },
    },
    footerLinks: [
      { labelKey: 'common.imprint', href: '/imprint' },
      { labelKey: 'common.privacy', href: '/privacy' },
      { labelKey: 'common.terms', href: '/terms' },
      // Hinweis auf CEN und DIN für Anwendungen der EN 16931 (Entscheid Torsten Nr. 428).
      { labelKey: 'common.licenses', href: '/licenses' },
    ],
    copyright: `${new Date().getFullYear()} Invoice-api.xhub.io`,
    // Schaltet den Footer auf die Drei-Zonen-Variante (Icon-Reihe + Rechtliches
    // links, Sprache/Theme + Copyright rechts). Pro SaaS eigene Liste; nur real
    // existierende Profile — keine Platzhalter erfinden.
    socialLinks: [
      { platform: 'website', href: 'https://invoice-api.xhub.io', label: 'invoice-api.xhub.io' },
      { platform: 'youtube', href: 'https://www.youtube.com/@invoice-api-xhub-io' },
      { platform: 'linkedin', href: 'https://www.linkedin.com/company/beeback-ug' },
      { platform: 'facebook', href: 'https://www.facebook.com/beebackug' },
      { platform: 'x', href: 'https://x.com/xhubioCompany' },
      { platform: 'github', href: 'https://github.com/xhubio' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Wrappers (locale provider + billing entitlements for RBAC + billing check)
  // ═══════════════════════════════════════════════════════════════════════════
  // LocaleWrapper outermost so the router tree can `useLocale()` for
  // date/number/currency formatting that adapts to the active org's country.
  // OnboardingCompletionListener bridges the YAML plugin's window event to
  // setPreference + useNavigate (must be inside the router + tRPC providers).
  // VerifactuNavGate innermost: arms the kernel's requiresModule gate so the
  // spanish-only VeriFactu entry stays out of every other org's sidebar.
  // ClarityAnalytics runs Microsoft Clarity app-wide once the analytics
  // category is granted (needs CookieConsentProvider, which wraps wrappers).
  wrappers: [
    LocaleWrapper,
    BillingEntitlementProvider,
    OnboardingCompletionListener,
    ClarityAnalytics,
    VerifactuNavGate,
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Cookie consent
  // ═══════════════════════════════════════════════════════════════════════════
  // v1.1 (kernel default v1.0): the analytics category now includes Clarity's
  // session recording — a new kind of processing, so everyone is asked again.
  cookieConsent: {
    version: 'v1.1',
    categories: cookieCategories,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Plugins
  // ═══════════════════════════════════════════════════════════════════════════
  plugins,

  // ═══════════════════════════════════════════════════════════════════════════
  // Routes
  // ═══════════════════════════════════════════════════════════════════════════
  routes: [
    // Public routes
    { path: '/terms', component: TermsPage, layout: 'public' },
    { path: '/privacy', component: PrivacyPage, layout: 'public' },
    { path: '/imprint', component: ImprintPage, layout: 'public' },
    { path: '/licenses', component: LicensesPage, layout: 'public' },
    { path: '/cookie-policy', component: CookiePolicyPage, layout: 'public' },
    // Abmelde-Link aus jeder Marketing-Mail — muss ohne Login erreichbar sein.
    { path: '/unsubscribe', component: UnsubscribePage, layout: 'public' },

    // Protected routes (wrapped with ConsentGuard)
    { path: '/profile', component: ProtectedProfilePage, layout: 'protected' },
    {
      path: '/settings',
      component: ProtectedSettingsProfilePage,
      layout: 'protected',
    },
    {
      path: '/settings/profile',
      component: ProtectedSettingsProfilePage,
      layout: 'protected',
    },
    {
      path: '/settings/password',
      component: ProtectedSettingsPasswordPage,
      layout: 'protected',
    },
    {
      path: '/settings/appearance',
      component: ProtectedSettingsAppearancePage,
      layout: 'protected',
    },
    {
      path: '/settings/account',
      component: ProtectedSettingsAccountPage,
      layout: 'protected',
    },
    // „System" gehoert der Organisation und liegt deshalb nicht mehr unter
    // `/settings` (= „Mein Konto" am Avatar). Die alten Adressen leiten weiter —
    // Lesezeichen, Support-Antworten und Changelog-Links zeigen noch dorthin.
    {
      path: '/system',
      component: ProtectedSettingsSystemPage,
      layout: 'protected',
    },
    { path: '/settings/system', component: redirectTo('/system'), layout: 'protected' },
    { path: '/settings/legal', component: redirectTo('/system/legal'), layout: 'protected' },
    {
      path: '/settings/email-log',
      component: redirectTo('/system/email-log'),
      layout: 'protected',
    },
    {
      path: '/settings/email-dispatch',
      component: redirectTo('/system/email-dispatch'),
      layout: 'protected',
    },
    {
      path: '/settings/email',
      component: ProtectedSettingsEmailPage,
      layout: 'protected',
    },
  ],
}
